/**
 * Attendance Hooks
 *
 * React Query hooks for attendance tracking functionality.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { attendanceService } from "../services";
import type {
  AttendanceFilters,
  CreateAttendanceRequest,
  CreateLessonSessionRequest,
  SessionFilters,
  UpdateAttendanceRequest,
  UpdateAttendancesRequest,
} from "../types/attendance";

// =====================================
// LessonSession Hooks
// =====================================

/**
 * Hook to fetch all lesson sessions
 */
export function useLessonSessions(filters?: SessionFilters) {
  return useQuery({
    queryKey: ["attendance", "sessions", filters],
    queryFn: () => attendanceService.getLessonSessions(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch sessions for a specific class group
 */
export function useSessionsByClassGroup(classGroupId: string) {
  return useQuery({
    queryKey: ["attendance", "sessions", "byClass", classGroupId],
    queryFn: () => attendanceService.getSessionsByClassGroup(classGroupId),
    enabled: !!classGroupId,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to fetch a single lesson session with attendances
 */
export function useLessonSession(id: string) {
  return useQuery({
    queryKey: ["attendance", "session", id],
    queryFn: () => attendanceService.getLessonSession(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 1, // 1 minute
  });
}

/**
 * Hook to create a new lesson session
 */
export function useCreateLessonSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLessonSessionRequest) =>
      attendanceService.createLessonSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "sessions"] });
    },
  });
}

/**
 * Hook to update a lesson session
 */
export function useUpdateLessonSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateLessonSessionRequest>;
    }) => attendanceService.updateLessonSession(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "sessions"] });
      queryClient.setQueryData(["attendance", "session", data.id], data);
    },
  });
}

/**
 * Hook to delete a lesson session
 */
export function useDeleteLessonSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => attendanceService.deleteLessonSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "sessions"] });
    },
  });
}

/**
 * Hook to bulk update attendances for a session
 */
export function useUpdateSessionAttendances() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      data,
    }: {
      sessionId: string;
      data: UpdateAttendancesRequest;
    }) => attendanceService.updateSessionAttendances(sessionId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "sessions"] });
      queryClient.setQueryData(["attendance", "session", data.id], data);
      queryClient.invalidateQueries({ queryKey: ["attendance", "records"] });
      queryClient.invalidateQueries({ queryKey: ["attendance", "summary"] });
    },
  });
}

// =====================================
// Attendance Record Hooks
// =====================================

/**
 * Hook to fetch attendance records
 */
export function useAttendanceRecords(filters?: AttendanceFilters) {
  return useQuery({
    queryKey: ["attendance", "records", filters],
    queryFn: () => attendanceService.getAttendanceRecords(filters),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to fetch a single attendance record
 */
export function useAttendanceRecord(id: string) {
  return useQuery({
    queryKey: ["attendance", "record", id],
    queryFn: () => attendanceService.getAttendanceRecord(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 1,
  });
}

/**
 * Hook to create an attendance record
 */
export function useCreateAttendanceRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAttendanceRequest) =>
      attendanceService.createAttendanceRecord(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "records"] });
      queryClient.invalidateQueries({ queryKey: ["attendance", "sessions"] });
      queryClient.invalidateQueries({ queryKey: ["attendance", "summary"] });
    },
  });
}

/**
 * Hook to update an attendance record
 */
export function useUpdateAttendanceRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAttendanceRequest }) =>
      attendanceService.updateAttendanceRecord(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "records"] });
      queryClient.setQueryData(["attendance", "record", data.id], data);
      queryClient.invalidateQueries({ queryKey: ["attendance", "sessions"] });
      queryClient.invalidateQueries({ queryKey: ["attendance", "summary"] });
    },
  });
}

/**
 * Hook to mark attendance as contacted
 */
export function useMarkAttendanceContacted() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => attendanceService.markAttendanceContacted(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["attendance", "records"] });
      queryClient.setQueryData(["attendance", "record", data.id], data);
      queryClient.invalidateQueries({ queryKey: ["attendance", "summary"] });
    },
  });
}

/**
 * Hook to fetch absences summary by student
 */
export function useAbsencesSummary(params?: {
  classGroup?: string;
  minAbsences?: number;
}) {
  return useQuery({
    queryKey: ["attendance", "summary", "absences", params],
    queryFn: () => attendanceService.getAbsencesSummary(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch recent absences
 */
export function useRecentAbsences(params?: {
  days?: number;
  classGroup?: string;
}) {
  return useQuery({
    queryKey: ["attendance", "summary", "recent", params],
    queryFn: () => attendanceService.getRecentAbsences(params),
    staleTime: 1000 * 60 * 2,
  });
}
